// SPDX-License-Identifier: GPL-3.0-only
import { multiplyMatrices } from './ghg.mjs';

// NuAnimBuffEvaluate_3, nucore_plain.cpp:1470; NuMtxSetRotateXYZ,
// NuMtxMulR, NuMtxPreScale and NuMtxScale in numtx.cpp. Arrays have the
// game's NUMTX memory order, which is column-major after transposition.
const identity = () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
const interval = Math.fround(2 * Math.PI / 32768);
function trig(angle, cosine = false) {
  // Native evaluation truncates radians to NUANG and indexes a 32768-entry
  // float sine table. Preserve its quantization instead of using free Euler.
  const units = Math.trunc(Math.fround(Math.fround(angle) * Math.fround(10430.378)));
  const index = ((units + (cosine ? 0x4000 : 0)) >> 1) & 0x7fff;
  return Math.fround(Math.sin(Math.fround(index * interval)));
}
function rotationMatrix([x, y, z]) {
  const cx = trig(x, true), sx = trig(x), cy = trig(y, true), sy = trig(y), cz = trig(z, true), sz = trig(z);
  return [cy * cz, cy * sz, -sy, 0,
    sx * sy * cz - cx * sz, sx * sy * sz + cx * cz, sx * cy, 0,
    cx * sy * cz + sx * sz, cx * sy * sz - sx * cz, cx * cy, 0,
    0, 0, 0, 1];
}
function matrix(value, label) {
  if (!value || value.length !== 16 || !Array.from(value).every(Number.isFinite)) throw new Error(`Invalid ${label} matrix`);
  return Array.from(value);
}
function vector(value, label) {
  if (!value || value.length !== 3 || !Array.from(value).every(Number.isFinite)) throw new Error(`Invalid pose ${label}`);
  return value;
}

// Accepts sampleAN3() output and parseGHG() output. Root-motion callbacks,
// procedural offsets and animation blending are upstream pose operations;
// this evaluates their resulting Euler pose, without inventing those systems.
export function evaluatePose(character, pose) {
  if (pose.rotationFormat !== 'euler') throw new Error('Unsupported pose rotation format');
  if (!Array.isArray(character.joints) || !Array.isArray(pose.nodes) || pose.nodes.length > character.joints.length)
    throw new Error('Pose/skeleton joint count mismatch');
  const localMatrices = [], absoluteMatrices = [], skinMatrices = [], inheritedScale = [];
  for (let i = 0; i < character.joints.length; i++) {
    const joint = character.joints[i], parent = joint.parent;
    if (!Number.isInteger(parent) || parent < -1 || parent >= i) throw new Error('Invalid skeleton parent order');
    const inverseBind = matrix(joint.inverseBindMatrix, 'inverse bind');
    if (i >= pose.nodes.length) {
      // Native evaluator clears unevaluated joints to identity, independently
      // of their hierarchy parent; it does not silently apply a rest pose.
      localMatrices.push(identity()); absoluteMatrices.push(identity());
      skinMatrices.push(inverseBind); inheritedScale.push([1, 1, 1]); continue;
    }
    const node = pose.nodes[i], flags = node.flags;
    if (!Number.isInteger(flags) || flags < 0 || flags > 255) throw new Error('Invalid pose flags');
    vector(node.rotation, 'rotation'); vector(node.translation, 'translation'); vector(node.scale, 'scale');
    let local = flags & 1 ? rotationMatrix(node.rotation) : identity();
    if (flags & 0x20) {
      const bind = matrix(joint.animationBindMatrix, 'animation bind');
      if (flags & 1) {
        local = multiplyMatrices(bind, local); // transpose(R * bind)
        local[3] = local[7] = local[11] = local[12] = local[13] = local[14] = 0;
        local[15] = 1; // NuMtxMulR explicitly discards translation.
      } else local = bind;
    }
    const parentScale = parent < 0 ? [1, 1, 1] : inheritedScale[parent];
    let scale = parentScale.slice();
    if (flags & 8) {
      // NuMtxPreScale scales the three row axes, leaving translation alone.
      for (let axis = 0; axis < 3; axis++) for (let c = 0; c < 3; c++) local[axis * 4 + c] *= node.scale[axis];
      scale = node.scale.map((s, axis) => s * parentScale[axis]);
    }
    if (flags & 0x10) {
      const inverse = parentScale.every(s => s !== 0) ? parentScale.map(s => 1 / s) : [0, 0, 0];
      scale = scale.map((s, axis) => s * inverse[axis]);
      // NuMtxScale scales columns, including any inherited bind translation.
      for (let row = 0; row < 4; row++) for (let axis = 0; axis < 3; axis++) local[row * 4 + axis] *= inverse[axis];
    }
    if (flags & 2) for (let axis = 0; axis < 3; axis++) local[12 + axis] += node.translation[axis];
    for (const index of [2, 6, 8, 9, 11, 14]) local[index] = -local[index];
    if (parent < 0 && (flags & 0x40)) scale = [1, 1, 1];
    inheritedScale.push(scale);
    const absolute = parent < 0 ? local.slice() : multiplyMatrices(absoluteMatrices[parent], local);
    if (!absolute.every(Number.isFinite)) throw new Error('Pose hierarchy overflow');
    localMatrices.push(local); absoluteMatrices.push(absolute);
    skinMatrices.push(multiplyMatrices(absolute, inverseBind));
  }
  const root = pose.nodes[0];
  const rootTranslation = root && (root.flags & 2) ? [root.translation[0], root.translation[1], -root.translation[2]] : [0, 0, 0];
  return { localMatrices, absoluteMatrices, worldMatrices: absoluteMatrices, skinMatrices, inheritedScale, rootTranslation };
}

export const evaluateAN3Pose = evaluatePose;

// ANI_SimpleAni3PlayerV4Joint_Blend's non-quaternion branch blends channels
// before hierarchy evaluation and ORs flags into the existing buffer. Its
// Euler wrap uses fixed-turn truncation, rather than quaternion/matrix lerp.
function wrapBlendRotation(delta) {
  delta = Math.fround(delta);
  if (delta > Math.fround(3.1415927) || delta < -Math.fround(3.1415927)) {
    let fixedTurn = Math.trunc(Math.fround(Math.fround(delta * 65536) / Math.fround(6.2831855))) & 0xffff;
    if (fixedTurn >= 0x8000) fixedTurn -= 0x10000;
    delta = Math.fround(fixedTurn * Math.fround(9.58738e-5));
  }
  return delta;
}

export function blendAN3Samples(from, to, alpha) {
  if (!Number.isFinite(alpha) || alpha < 0 || alpha > 1) throw new Error('Animation blend alpha must be in [0, 1]');
  if (from.rotationFormat !== 'euler' || to.rotationFormat !== 'euler') throw new Error('Unsupported blend rotation format');
  if (!Array.isArray(from.nodes) || !Array.isArray(to.nodes) || from.nodes.length !== to.nodes.length)
    throw new Error('Animation blend joint count mismatch');
  const blend = Math.fround(alpha), inverse = Math.fround(1 - blend);
  const nodes = from.nodes.map((a, i) => {
    const b = to.nodes[i];
    for (const n of [a, b]) {
      if (!Number.isInteger(n.flags) || n.flags < 0 || n.flags > 255) throw new Error('Invalid blend node flags');
      for (const field of ['translation', 'rotation', 'scale']) vector(n[field], field);
    }
    const output = { flags: a.flags | b.flags };
    for (const [field, mask] of [['translation', 2], ['rotation', 1], ['scale', 8]]) {
      output[field] = a[field].map((value, component) => {
        const existing = Math.fround(value);
        if (b.flags & mask) {
          let delta = Math.fround(Math.fround(b[field][component]) - existing);
          if (field === 'rotation') delta = wrapBlendRotation(delta);
          return Math.fround(existing + Math.fround(delta * blend));
        }
        // Missing target groups are identity: zero translation/rotation and
        // unit scale. Native rotation-to-identity does not wrap its delta.
        const attenuated = Math.fround(existing * inverse);
        return field === 'scale' ? Math.fround(attenuated + blend) : attenuated;
      });
    }
    return output;
  });
  const root = to.nodes[0];
  // Native blend's root-motion output is the unblended target translation.
  const rootTranslation = root && (root.flags & 2) ? [root.translation[0], root.translation[1], -root.translation[2]] : [0, 0, 0];
  return { frame: to.frame, rotationFormat: 'euler', nodes, rootTranslation };
}
